import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { Building2, History, LayoutDashboard, Menu, Settings, Users, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { BarraFiltros, type ChipFiltro } from '@/componentes/BarraFiltros';
import { Botao, BotaoDestrutivo } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoTexto } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra, Carregando, EsqueletoDeCampo } from '@/componentes/Esqueleto';
import { Janela } from '@/componentes/JanelaDeAcao';
import { AssinaturaAtenas, CoMarcaLogin, Marca } from '@/componentes/Marca';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { RodapeTabela, Tabela, type Coluna } from '@/componentes/Tabela';
import { HistoriaNaMesa } from '@/historias/mesa';
import { formatarData, formatarDataHora, formatarInteiro } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { anunciar, retornoDe } from '@/lib/retorno';
import { SituacaoDePessoa } from '@/modulos/cadastros/Avisos';
import { enderecoDoConvite } from '@/modulos/cadastros/convite';
import { CHAVE_DA_SESSAO } from '@/modulos/sessao/consultas';

import { api, type AdministradorDaPlataforma, type AtoDaPlataforma, type EmpresaDaPlataforma } from './api';
import { ConfiguracoesDoSistema } from './ConfiguracoesDoSistema';
import {
  acessoDoAdministrador,
  complementoDoAto,
  enderecoDoConviteDePlataforma,
  filtrarPorNome,
  ROTULO_DA_SECAO,
  rotuloDoAto,
  SECAO_PADRAO,
  SECOES,
  validarBuscaDaPlataforma,
  type BuscaDaPlataforma,
  type Secao,
} from './gestao';
import { GestaoDaEmpresa } from './GestaoDaEmpresa';
import { usePaginacao } from './paginacao';
import { Bloco, EnderecoParaEntregar, Recado } from './Pecas';

/**
 * A administração da plataforma: quem administra o SISTEMA, acima das
 * empresas.
 *
 * A decisão 21 (0029) deu criar empresa e reemitir o convite do administrador
 * dela. A decisão 25 (0032) fez disto um sistema de gestão com módulos
 * próprios: visão geral, empresas — com as pessoas, os papéis, o convite, a
 * troca de senha, as sessões e a situação de cada uma —, os administradores da
 * plataforma e a trilha do que a plataforma fez. Desde 27/09/2026 (0042), também
 * as configurações do sistema: o motor de IA, que vale para todas as empresas.
 *
 * Roda fora da casca e com sessão PRÓPRIA (`ctf_plataforma`): ela não abre
 * nenhuma tela de empresa, e a sessão de empresa não abre esta. Gere ACESSO,
 * e não OPERAÇÃO: frota, O.S., combustível e custo de uma empresa continuam
 * só para quem é de dentro dela.
 *
 * A casca é própria, mas segue os mesmos pontos de quebra da casca do sistema
 * (R01, p.28): menu aberto de 248 px a partir de 1280, só ícones (72 px) entre
 * 1024 e 1279, gaveta pelo ☰ abaixo de 1024; margens pela `pagina`.
 */

const CHAVE = ['plataforma'] as const;
const rota = getRouteApi('/plataforma');

/** "1 empresa", "3 empresas": a contagem flexiona o número (p.22–23). */
function contagem(quantidade: number, um: string, muitos: string): string {
  return `${formatarInteiro(quantidade)} ${quantidade === 1 ? um : muitos}`;
}

export function PaginaPlataforma() {
  const eu = useQuery({
    queryKey: [...CHAVE, 'eu'],
    queryFn: () => api.eu(),
    retry: false,
  });

  if (eu.data) return <Administracao nome={eu.data.nome} email={eu.data.email} contaNaEmpresa={eu.data.conta?.empresa ?? null} />;

  return (
    <div className="h-full overflow-auto bg-tela">
      <div className="mx-auto flex max-w-[960px] flex-col gap-8 p-12">
        <CoMarcaLogin />
        {eu.isPending ? <ConferindoASessao /> : null}
        {eu.isError ? (
          <ErroDaLista
            oQue="Os dados da sua sessão"
            erro={eu.error}
            aoTentarDeNovo={() => {
              void eu.refetch();
            }}
          />
        ) : null}
        {eu.data === null ? <Entrada /> : null}
      </div>
    </div>
  );
}

/**
 * Conferindo a sessão (p.32): a forma da entrada, que é o que esta moldura
 * desenha — o cabeçalho, que vale para os dois desfechos, e o bloco dos dois
 * campos. Com a sessão aberta, a administração toma a tela inteira.
 */
function ConferindoASessao() {
  return (
    <>
      <CabecalhoPagina sobretitulo="CT Frotas" titulo="Administração da plataforma" subtitulo="Para quem administra o sistema, acima das empresas" />
      <Carregando oQue="Conferindo a sessão da plataforma" className="cartao flex flex-col gap-4 p-6">
        <Barra className="my-1 h-5 w-56 max-w-full" />
        <EsqueletoDeCampo />
        <EsqueletoDeCampo />
        <Barra className="ml-auto h-[var(--controle-m)] w-48 rounded-controle" />
        <Barra className="my-1 h-3 w-4/5" />
      </Carregando>
    </>
  );
}

function Entrada() {
  const cliente = useQueryClient();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const entrar = useMutation({
    mutationFn: () => api.entrar(email.trim(), senha),
    /* Entrar é a própria troca de tela, sem toast; a recusa fica no aviso do
       topo do formulário (p.22), e não repete no toast. */
    meta: { silencioso: true, silenciarFalha: true },
    onSuccess: () => {
      setSenha('');
      void cliente.invalidateQueries({ queryKey: CHAVE });
    },
  });
  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (email.trim() === '' || senha === '') return;
    entrar.mutate();
  }
  return (
    <>
      <CabecalhoPagina sobretitulo="CT Frotas" titulo="Administração da plataforma" subtitulo="Para quem administra o sistema, acima das empresas" />
      <Bloco titulo="Entrar na administração">
        <form className="flex flex-col gap-4" onSubmit={enviar}>
          {entrar.isError ? <Recado erro={explicarFalha(entrar.error)} /> : null}
          <CampoTexto rotulo="E-mail" valor={email} tipo="email" autoComplete="username" maxLength={320} aoMudar={setEmail} />
          <CampoTexto rotulo="Senha" valor={senha} tipo="senha" autoComplete="current-password" maxLength={200} aoMudar={setSenha} />
          <div className="flex justify-end">
            <Botao nivel="primario" type="submit" disabled={entrar.isPending}>
              {entrar.isPending ? 'Entrando…' : 'Entrar na administração'}
            </Botao>
          </div>
        </form>
        <p className="txt-apoio text-tinta-secundaria">
          Esta entrada não é a das empresas. Quem trabalha numa empresa entra pela tela de entrada do sistema.
        </p>
      </Bloco>
    </>
  );
}

/**
 * O ícone de cada seção, do vocabulário da v2.1 (p.19): visão geral é o
 * painel, empresa é `building-2`, administradores são pessoas, a trilha é o
 * histórico e as configurações têm entrada própria (`settings`) — as duas que
 * faltavam no vocabulário da 1.0, e que a v2.1 trouxe.
 */
const ICONE_DA_SECAO: Readonly<Record<Secao, LucideIcon>> = {
  visao: LayoutDashboard,
  empresas: Building2,
  administradores: Users,
  trilha: History,
  configuracoes: Settings,
};

/* O mesmo item da navegação do sistema: com rótulo de 1280 para cima, só o
   ícone entre 1024 e 1279, e rótulo de novo dentro da gaveta. */
const ITEM =
  'txt-corpo flex h-[var(--controle-m)] w-full cursor-pointer items-center gap-3 rounded-controle px-3 text-left max-[1280px]:justify-center max-[1280px]:px-0 group-data-[gaveta=aberta]/nav:justify-start! group-data-[gaveta=aberta]/nav:px-3!';

function Administracao({
  nome,
  email,
  contaNaEmpresa,
}: {
  readonly nome: string;
  readonly email: string;
  /** A empresa da conta de suporte vinculada (0134): o cabeçalho leva até ela sem entrar de novo. */
  readonly contaNaEmpresa: string | null;
}) {
  const cliente = useQueryClient();
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();
  const secao = busca.secao ?? SECAO_PADRAO;
  const [gaveta, setGaveta] = useState(false);

  function irPara(proxima: BuscaDaPlataforma) {
    void navegar({ search: validarBuscaDaPlataforma({ ...proxima }) });
  }

  const sair = useMutation({
    mutationFn: () => api.sair(),
    /* Sair é a própria troca de tela: a entrada da plataforma aparece. */
    meta: { silencioso: true },
    /*
     * A SESSAO VIRA "NENHUMA" NA HORA, e nao "apagada e reconsultada". O
     * desenho anterior removia as consultas e depois as invalidava — mas
     * invalidar uma consulta ja removida nao reconsulta nada: a tela ficava
     * com o administrador velho na memoria e as listas recusadas pelo
     * servidor ("Os dados da empresa nao carregaram"), sem nunca voltar a
     * entrada (achado do dono no QAS, 24/10/2026). Agora `eu` passa a `null`,
     * que desenha a Entrada, o resto sai do cache e o endereco perde a
     * empresa aberta.
     */
    onSuccess: () => {
      cliente.setQueryData([...CHAVE, 'eu'], null);
      cliente.removeQueries({ queryKey: CHAVE, predicate: (q) => q.queryKey[1] !== 'eu' });
      void navegar({ search: validarBuscaDaPlataforma({}) });
    },
    onError: (erro) => {
      anunciar({
        tom: 'falha',
        titulo: 'Você não saiu da administração',
        comoResolver: explicarFalha(erro).comoResolver,
      });
    },
  });

  return (
    <div className="flex h-full flex-col bg-tela">
      {/* O cabeçalho QUEBRA em vez de estourar (R01, p.28): abaixo de 1024 px
          o ☰ e o título ficam na primeira linha, quem está dentro e a saída
          na segunda. A assinatura da Atenas some abaixo de 1280, como na
          barra do sistema. */}
      <header className="flex min-h-[var(--barra-superior)] shrink-0 flex-wrap items-center gap-x-6 gap-y-2 border-b border-borda bg-card px-6 py-3 max-[1024px]:gap-x-4 max-[768px]:px-4">
        <div className="flex min-w-0 flex-1 items-center gap-2 max-[1024px]:basis-full">
          <button
            type="button"
            aria-label="Abrir o menu"
            title="Abrir o menu"
            aria-expanded={gaveta}
            onClick={() => {
              setGaveta(true);
            }}
            className="botao botao-terciario hidden max-[1024px]:inline-flex"
          >
            <Menu aria-hidden="true" className="size-[var(--icone-controle)]" />
          </button>
          <span className="txt-subtitulo min-w-0 truncate text-tinta">Administração da plataforma</span>
        </div>
        <AssinaturaAtenas />
        <span className="ml-auto flex min-w-0 flex-col items-end">
          <span className="txt-corpo text-tinta">{nome}</span>
          <span className="txt-apoio text-tinta-secundaria max-[768px]:hidden">{email}</span>
        </span>
        {/* A TROCA DE AMBIENTE da conta de suporte (0134, decisão 62): a
            entrada com a senha unificada abriu a sessão da empresa junto. Se
            ela tiver vencido, o sistema mostra a entrada da empresa. */}
        {contaNaEmpresa === null ? null : (
          <Link
            to="/"
            className="botao botao-terciario min-w-0"
            title={`Ir para ${contaNaEmpresa}, sem entrar de novo`}
            onClick={() => {
              void cliente.invalidateQueries({ queryKey: CHAVE_DA_SESSAO });
            }}
          >
            <Building2 aria-hidden="true" className="size-[var(--icone-controle)] shrink-0" />
            <span className="truncate">Ir para {contaNaEmpresa}</span>
          </Link>
        )}
        <Botao
          nivel="secundario"
          onClick={() => {
            sair.mutate();
          }}
        >
          Sair da administração
        </Botao>
      </header>

      <div className="flex min-h-0 flex-1">
        <MenuDaPlataforma
          secao={busca.empresa ? 'empresas' : secao}
          irPara={irPara}
          gavetaAberta={gaveta}
          aoFecharGaveta={() => {
            setGaveta(false);
          }}
        />

        <main className="flex min-h-0 min-w-0 flex-1">
          {busca.empresa ? (
            <GestaoDaEmpresa
              key={busca.empresa}
              empresaId={busca.empresa}
              aoVoltar={() => {
                irPara({ secao: 'empresas' });
              }}
            />
          ) : (
            <div className="min-w-0 flex-1 overflow-auto">
              <div className="pagina">
                <Conteudo
                  secao={secao}
                  termo={busca.termo ?? ''}
                  irPara={irPara}
                />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

/**
 * O menu da plataforma, com os pontos de quebra da navegação do sistema (R01,
 * p.28) e o item ativo da R02 (p.31): laranja 50 com contorno de 1 px laranja
 * 500. Abaixo de 1024 px é gaveta: véu a 40%, `Esc` e clique no véu fecham, o
 * foco entra no primeiro item e volta ao ☰ ao fechar.
 */
function MenuDaPlataforma({
  secao,
  irPara,
  gavetaAberta,
  aoFecharGaveta,
}: {
  readonly secao: Secao;
  readonly irPara: (busca: BuscaDaPlataforma) => void;
  readonly gavetaAberta: boolean;
  readonly aoFecharGaveta: () => void;
}) {
  const menu = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!gavetaAberta) return;
    const origem = document.activeElement;
    menu.current?.querySelector<HTMLElement>('button')?.focus();
    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key === 'Escape') aoFecharGaveta();
    }
    const largo = window.matchMedia('(min-width: 1024px)');
    function aoMudarLargura() {
      if (largo.matches) aoFecharGaveta();
    }
    document.addEventListener('keydown', aoTeclar);
    largo.addEventListener('change', aoMudarLargura);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
      largo.removeEventListener('change', aoMudarLargura);
      if (origem instanceof HTMLElement && origem.getClientRects().length > 0) origem.focus();
    };
  }, [gavetaAberta, aoFecharGaveta]);

  return (
    <>
      {gavetaAberta ? (
        <div aria-hidden className="fixed inset-0 z-30 hidden bg-sobreposicao max-[1024px]:block" onClick={aoFecharGaveta} />
      ) : null}
      <nav
        ref={menu}
        aria-label="Módulos da administração da plataforma"
        data-gaveta={gavetaAberta ? 'aberta' : 'fechada'}
        className="group/nav flex w-[var(--menu-largura)] shrink-0 flex-col gap-6 overflow-auto border-r border-borda bg-rebaixada p-2 max-[1280px]:w-[var(--menu-trilho)] max-[1024px]:hidden data-[gaveta=aberta]:max-[1024px]:fixed data-[gaveta=aberta]:max-[1024px]:inset-y-0 data-[gaveta=aberta]:max-[1024px]:left-0 data-[gaveta=aberta]:max-[1024px]:z-40 data-[gaveta=aberta]:max-[1024px]:flex data-[gaveta=aberta]:max-[1024px]:w-[var(--menu-largura)]"
      >
        <Marca />
        <div className="flex flex-col gap-1">
          <span className="txt-rotulo px-3 py-2 text-tinta-secundaria max-[1280px]:hidden group-data-[gaveta=aberta]/nav:block!">
            Gestão da plataforma
          </span>
          <ul className="flex flex-col gap-1">
            {SECOES.map((s) => {
              const Icone = ICONE_DA_SECAO[s];
              const ativa = secao === s;
              return (
                <li key={s}>
                  <button
                    type="button"
                    title={ROTULO_DA_SECAO[s]}
                    aria-current={ativa ? 'page' : undefined}
                    className={`${ITEM} ${ativa ? 'bg-selecao text-tinta outline-1 -outline-offset-1 outline-selecao-contorno' : 'text-tinta-secundaria hover:bg-card'}`}
                    onClick={() => {
                      irPara({ secao: s });
                      aoFecharGaveta();
                    }}
                  >
                    <Icone className="size-[var(--icone-menu)] shrink-0" />
                    <span className="max-[1280px]:sr-only group-data-[gaveta=aberta]/nav:not-sr-only!">{ROTULO_DA_SECAO[s]}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <p className="txt-apoio px-3 text-tinta-secundaria max-[1280px]:hidden group-data-[gaveta=aberta]/nav:block!">
          A plataforma gere quem entra em cada empresa. O que há dentro dela — frota, O.S., combustível, custo — só quem é de dentro vê.
        </p>
      </nav>
    </>
  );
}

function Conteudo({
  secao,
  termo,
  irPara,
}: {
  readonly secao: Secao;
  readonly termo: string;
  readonly irPara: (b: BuscaDaPlataforma) => void;
}) {
  switch (secao) {
    case 'visao':
      return <VisaoGeral irPara={irPara} />;
    case 'empresas':
      return <Empresas termo={termo} irPara={irPara} />;
    case 'administradores':
      return <Administradores />;
    case 'trilha':
      return <Trilha />;
    case 'configuracoes':
      return <ConfiguracoesDoSistema />;
  }
}

/**
 * A ação de uma linha (p.18): à direita e visível no hover ou no foco da
 * linha; em tela de toque, sempre visível. O clique não chega à linha, que
 * abre a empresa.
 */
function AcaoDaLinha({ children }: { readonly children: ReactNode }) {
  return (
    <span
      className="inline-flex text-left group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:hover)]:opacity-0"
      onClick={(evento) => {
        evento.stopPropagation();
      }}
    >
      {children}
    </span>
  );
}

// ------------------------------------------------------------ visão geral

function VisaoGeral({ irPara }: { readonly irPara: (b: BuscaDaPlataforma) => void }) {
  const empresas = useQuery({ queryKey: [...CHAVE, 'empresas'], queryFn: () => api.empresas() });
  const administradores = useQuery({ queryKey: [...CHAVE, 'administradores'], queryFn: () => api.administradores() });
  const trilha = useQuery({ queryKey: [...CHAVE, 'trilha'], queryFn: () => api.trilha() });

  const semana = Date.now() - 7 * 24 * 3600 * 1000;
  const atosDaSemana = (trilha.data ?? []).filter((a) => new Date(a.ocorridoEm).getTime() >= semana).length;
  const pessoas = (empresas.data ?? []).reduce((soma, e) => soma + e.pessoas, 0);
  const admsAtivos = (administradores.data ?? []).filter((a) => a.ativo).length;
  const carregado = empresas.data && administradores.data && trilha.data;
  const falha = empresas.error ?? administradores.error ?? trilha.error;

  return (
    <>
      <CabecalhoPagina
        sobretitulo="Administração da plataforma"
        titulo="Visão geral"
        subtitulo="O sistema inteiro visto por cima: empresas, pessoas que entram, quem administra e o que a plataforma fez."
      />
      {falha ? (
        <ErroDaLista
          oQue="Os números da plataforma"
          erro={falha}
          aoTentarDeNovo={() => {
            void empresas.refetch();
            void administradores.refetch();
            void trilha.refetch();
          }}
        />
      ) : null}
      {carregado ? (
        <FaixaIndicadores>
          <CardIndicador
            rotulo="Empresas"
            valor={formatarInteiro(empresas.data.length)}
            contexto="no sistema"
            /* O vocabulário não tem "empresa"; o mais próximo é o local. */
            icone={Building2}
            tom="marca"
            aoAbrir={() => {
              irPara({ secao: 'empresas' });
            }}
          />
          <CardIndicador
            rotulo="Pessoas ativas"
            valor={formatarInteiro(pessoas)}
            contexto="somando todas as empresas"
            icone={Users}
            tom="operando"
          />
          <CardIndicador
            rotulo="Administradores"
            valor={formatarInteiro(admsAtivos)}
            contexto={`${admsAtivos === 1 ? 'ativo' : 'ativos'}, de ${contagem(administradores.data.length, 'convidado', 'convidados')}`}
            icone={Users}
            {...(administradores.data.length > 0 ? { proporcao: admsAtivos / administradores.data.length } : {})}
            aoAbrir={() => {
              irPara({ secao: 'administradores' });
            }}
          />
          <CardIndicador
            rotulo="Atos na plataforma"
            valor={formatarInteiro(atosDaSemana)}
            contexto="nos últimos 7 dias"
            icone={History}
            aoAbrir={() => {
              irPara({ secao: 'trilha' });
            }}
          />
        </FaixaIndicadores>
      ) : !falha ? (
        /* Carregando (p.32): o rótulo e o ícone de cada card já se sabem; só o
           número vira esqueleto. */
        <FaixaIndicadores>
          <CardIndicador carregando rotulo="Empresas" valor="" contexto="" icone={Building2} tom="marca" />
          <CardIndicador carregando rotulo="Pessoas ativas" valor="" contexto="" icone={Users} tom="operando" />
          <CardIndicador carregando rotulo="Administradores" valor="" contexto="" icone={Users} />
          <CardIndicador carregando rotulo="Atos na plataforma" valor="" contexto="" icone={History} />
        </FaixaIndicadores>
      ) : null}

      <Bloco
        titulo="Últimos atos da plataforma"
        acoes={
          <Botao
            nivel="terciario"
            onClick={() => {
              irPara({ secao: 'trilha' });
            }}
          >
            Abrir a trilha
          </Botao>
        }
      >
        <TabelaDaTrilha
          atos={(trilha.data ?? []).slice(0, 8)}
          carregando={trilha.isPending}
          erro={
            trilha.isError ? (
              <ErroDaLista oQue="Os atos da trilha" erro={trilha.error} aoTentarDeNovo={() => void trilha.refetch()} />
            ) : null
          }
        />
      </Bloco>
    </>
  );
}

// ------------------------------------------------------------ empresas

interface ConviteEmitido {
  readonly empresa: string;
  readonly email: string;
  readonly codigo: string;
  readonly expiraEm: string;
}

function Empresas({ termo, irPara }: { readonly termo: string; readonly irPara: (b: BuscaDaPlataforma) => void }) {
  const cliente = useQueryClient();
  const [criando, setCriando] = useState(false);
  const [convite, setConvite] = useState<ConviteEmitido | null>(null);

  const empresas = useQuery({ queryKey: [...CHAVE, 'empresas'], queryFn: () => api.empresas() });
  const desativadas = useQuery({ queryKey: [...CHAVE, 'desativadas'], queryFn: () => api.empresasDesativadas() });
  const foraDoAr = new Set(desativadas.data ?? []);
  const reemitir = useMutation({
    mutationFn: async (e: EmpresaDaPlataforma) => ({ empresa: e.nome, ...(await api.reemitirConvite(e.empresaId)) }),
    /* Retorno de ação é toast (p.30, p.31); o endereço novo, que aparece uma
       vez só, continua no bloco de entregar. A recusa sai pelo toast de falha. */
    meta: {
      retorno: retornoDe<{ empresa: string; email: string; expiraEm: string }>((r) => ({
        titulo: 'Convite do administrador reemitido',
        alvo: { nome: r.empresa },
        fatos: [r.email, `vale até ${formatarDataHora(new Date(r.expiraEm))}`],
        efeito: 'O convite anterior deixou de valer',
      })),
    },
    onSuccess: (r) => {
      setConvite(r);
      void cliente.invalidateQueries({ queryKey: CHAVE });
    },
  });

  /* A situação de cada empresa vem em outra consulta: sem esperar por ela, a
     empresa desativada aparecia sem o selo e o ganhava um instante depois. */
  const carregando = empresas.isPending || desativadas.isPending;
  const todas = empresas.data ?? [];
  const lista = filtrarPorNome(todas, termo);
  const paginacao = usePaginacao(lista);

  const chips: readonly ChipFiltro[] = termo
    ? [
        {
          id: 'termo',
          rotulo: `Busca: ${termo}`,
          aoRemover: () => {
            irPara({ secao: 'empresas' });
          },
        },
      ]
    : [];

  const colunas: readonly Coluna<EmpresaDaPlataforma>[] = [
    {
      chave: 'nome',
      rotulo: 'Empresa',
      celula: (e) =>
        foraDoAr.has(e.empresaId) ? (
          <span className="flex items-center gap-2">
            <span>{e.nome}</span>
            <SituacaoDePessoa ativo={false} />
          </span>
        ) : (
          e.nome
        ),
    },
    { chave: 'criada', rotulo: 'No sistema desde', celula: (e) => formatarData(new Date(e.criadoEm)) },
    { chave: 'pessoas', rotulo: 'Pessoas ativas', alinhamento: 'numero', celula: (e) => formatarInteiro(e.pessoas) },
    {
      /* Reemitir ANULA o convite aberto: é ação da linha, à direita e no
         hover (p.18), e com confirmação que diz isso (p.15). */
      chave: 'convite',
      rotulo: 'Administrador da empresa',
      alinhamento: 'numero',
      celula: (e) => (
        <AcaoDaLinha>
          <BotaoDestrutivo
            rotulo="Reemitir convite"
            pergunta={`Reemitir o convite do administrador de ${e.nome}?`}
            consequencia="O convite aberto deixa de valer, e só o endereço novo, que aparece em seguida, abre a primeira entrada."
            rotuloConfirmar="Reemitir convite"
            rotuloCancelar="Manter o convite"
            {...(reemitir.isPending ? { motivoIndisponivel: 'Espere o pedido anterior terminar.' } : {})}
            aoConfirmar={() => {
              reemitir.mutate(e);
            }}
          />
        </AcaoDaLinha>
      ),
    },
  ];

  return (
    <>
      <CabecalhoPagina
        sobretitulo="Administração da plataforma"
        titulo="Empresas"
        subtitulo="Abra uma empresa para gerir as pessoas dela: cadastro, papel, convite, troca de senha, sessões e situação."
        acoes={
          <HistoriaNaMesa codigo="BTN-PLT-01" className="flex">
            <Botao
              nivel="primario"
              onClick={() => {
                setCriando(true);
              }}
            >
              Criar empresa
            </Botao>
          </HistoriaNaMesa>
        }
      />

      {convite ? (
        <EnderecoParaEntregar
          titulo={`Convite do administrador de ${convite.empresa}`}
          endereco={enderecoDoConvite(window.location.origin, convite.codigo)}
          para={convite.email}
          expiraEm={convite.expiraEm}
          depois="Quem abrir escolhe a senha do administrador desta empresa."
          aoFechar={() => {
            setConvite(null);
          }}
        />
      ) : null}

      <BarraFiltros
        chips={chips}
        /* Sem a frase "Carregando" no lugar da contagem (p.32): barra até a
           lista chegar. */
        contagem={
          carregando
            ? null
            : empresas.data
              ? `${formatarInteiro(lista.length)} de ${contagem(todas.length, 'empresa', 'empresas')}`
              : ''
        }
        aoLimpar={() => {
          irPara({ secao: 'empresas' });
        }}
      >
        <div className="min-w-0 flex-1">
          <CampoTexto
            rotulo="Buscar empresa"
            valor={termo}
            placeholder="Parte do nome"
            aplicado={termo !== ''}
            aoMudar={(v) => {
              irPara({ secao: 'empresas', termo: v });
            }}
          />
        </div>
      </BarraFiltros>

      <Tabela
        rotulo="Empresas no sistema"
        colunas={colunas}
        linhas={paginacao.visiveis}
        chaveDaLinha={(e) => e.empresaId}
        carregando={carregando}
        erro={
          empresas.isError ? (
            <ErroDaLista oQue="As empresas" erro={empresas.error} aoTentarDeNovo={() => void empresas.refetch()} />
          ) : null
        }
        aoSelecionar={(e) => {
          irPara({ empresa: e.empresaId });
        }}
        vazio={
          termo ? (
            <EstadoVazio
              titulo="Nenhuma empresa com esses filtros"
              descricao={`Nenhuma empresa tem “${termo}” no nome. Limpe a busca para ver todas.`}
              acao={
                <Botao
                  nivel="secundario"
                  onClick={() => {
                    irPara({ secao: 'empresas' });
                  }}
                >
                  Limpar filtros
                </Botao>
              }
            />
          ) : (
            <EstadoVazio
              titulo="Nenhuma empresa ainda"
              descricao="Crie a primeira empresa: ela nasce com os papéis da matriz e o convite do administrador dela."
              acao={
                <Botao
                  nivel="secundario"
                  onClick={() => {
                    setCriando(true);
                  }}
                >
                  Criar empresa
                </Botao>
              }
            />
          )
        }
        rodape={<RodapeTabela {...paginacao.rodape} />}
      />

      <Janela
        titulo="Criar empresa"
        aberta={criando}
        aoFechar={() => {
          setCriando(false);
        }}
      >
        <CriacaoDeEmpresa
          aoCancelar={() => {
            setCriando(false);
          }}
          aoCriar={(c) => {
            setConvite(c);
            setCriando(false);
          }}
        />
      </Janela>
    </>
  );
}

/** Criar empresa, na janela (R01, p.29: três campos, tarefa curta). */
function CriacaoDeEmpresa({
  aoCancelar,
  aoCriar,
}: {
  readonly aoCancelar: () => void;
  readonly aoCriar: (convite: ConviteEmitido) => void;
}) {
  const cliente = useQueryClient();
  const [empresa, setEmpresa] = useState('');
  const [admNome, setAdmNome] = useState('');
  const [admEmail, setAdmEmail] = useState('');
  const criar = useMutation({
    mutationFn: () => api.criarEmpresa(empresa.trim(), { nome: admNome.trim(), email: admEmail.trim() }),
    /* A recusa fica no aviso do topo do formulário (p.22), e não repete no toast. */
    meta: {
      retorno: retornoDe<{ expiraEm: string }>((r) => ({
        titulo: 'Empresa criada e convite emitido',
        alvo: { nome: empresa.trim() },
        fatos: [`administração: ${admNome.trim()}`, admEmail.trim().toLowerCase()],
        efeito: `O convite vale até ${formatarDataHora(new Date(r.expiraEm))}`,
      })),
      silenciarFalha: true,
    },
    onSuccess: (r) => {
      aoCriar({ empresa: empresa.trim(), email: admEmail.trim().toLowerCase(), codigo: r.codigo, expiraEm: r.expiraEm });
      void cliente.invalidateQueries({ queryKey: CHAVE });
    },
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    criar.mutate();
  }

  return (
    <form className="formulario-grade" onSubmit={enviar}>
      {criar.isError ? (
        <div className="campo-linha">
          <Recado erro={explicarFalha(criar.error)} />
        </div>
      ) : null}
      <div className="campo-largo">
        <CampoTexto rotulo="Nome da empresa" valor={empresa} maxLength={200} aoMudar={setEmpresa} />
      </div>
      <div className="campo-largo">
        <CampoTexto rotulo="Nome de quem vai administrar a empresa" valor={admNome} maxLength={120} aoMudar={setAdmNome} />
      </div>
      <CampoTexto
        rotulo="E-mail de quem vai administrar a empresa"
        valor={admEmail}
        tipo="email"
        maxLength={320}
        ajuda="A pessoa recebe o convite, escolhe a própria senha e entra com este e-mail."
        aoMudar={setAdmEmail}
      />
      <p className="txt-apoio campo-linha text-tinta-secundaria">
        A empresa nasce com os nove papéis da matriz aprovada, as causas de parada padrão e o administrador dela. Filiais, obras e frota são cadastradas por quem é de dentro; as pessoas, por ela ou por aqui.
      </p>
      <div className="barra-de-acoes campo-linha">
        <Botao nivel="secundario" onClick={aoCancelar}>
          Voltar sem criar
        </Botao>
        <Botao nivel="primario" type="submit" disabled={criar.isPending}>
          {criar.isPending ? 'Criando…' : 'Criar empresa'}
        </Botao>
      </div>
    </form>
  );
}

// ------------------------------------------------------------ administradores

function Administradores() {
  const cliente = useQueryClient();
  const [convidando, setConvidando] = useState(false);
  const [convite, setConvite] = useState<{ email: string; codigo: string; expiraEm: string } | null>(null);

  const lista = useQuery({ queryKey: [...CHAVE, 'administradores'], queryFn: () => api.administradores() });
  const situacao = useMutation({
    mutationFn: (a: AdministradorDaPlataforma) => api.situacaoDoAdministrador(a.administradorId, !a.ativo),
    /* Retorno de ação é toast (p.30, p.31); a recusa sai pelo toast de falha. */
    meta: {
      retorno: retornoDe<{ ativo: boolean }, AdministradorDaPlataforma>((r, a) => ({
        titulo: r.ativo ? 'Administrador reativado' : 'Administrador desativado',
        alvo: { nome: a.nome },
        fatos: [a.email],
      })),
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE });
    },
  });
  const paginacao = usePaginacao(lista.data ?? []);

  const agora = new Date();
  const colunas: readonly Coluna<AdministradorDaPlataforma>[] = [
    {
      chave: 'quem',
      rotulo: 'Administrador',
      celula: (a) => (
        <span className="flex flex-col">
          <span className="txt-corpo text-tinta">{a.eu ? `${a.nome} (você)` : a.nome}</span>
          <span className="txt-apoio text-tinta-secundaria">{a.email}</span>
        </span>
      ),
    },
    {
      chave: 'acesso',
      rotulo: 'Acesso',
      celula: (a) => {
        const s = acessoDoAdministrador(a, agora);
        return (
          <span className="flex flex-col">
            <span className="txt-corpo text-tinta">{s.rotulo}</span>
            <span className="txt-apoio text-tinta-secundaria">{s.detalhe}</span>
          </span>
        );
      },
    },
    { chave: 'desde', rotulo: 'Convidado em', celula: (a) => formatarData(new Date(a.criadoEm)) },
    { chave: 'situacao', rotulo: 'Situação', historia: 'SEL-PLT-04', alinhamento: 'status', celula: (a) => <SituacaoDePessoa ativo={a.ativo} /> },
    {
      /* A ação da linha, à direita e no hover (p.18). A frase de quem é você
         não é ação: fica sempre à vista. */
      chave: 'acao',
      rotulo: 'Ação',
      alinhamento: 'numero',
      celula: (a) =>
        a.eu ? (
          <span className="txt-apoio text-tinta-secundaria">Outro administrador muda a sua</span>
        ) : (
          <AcaoDaLinha>
            {a.ativo ? (
              <HistoriaNaMesa codigo="BTN-PLT-03" className="flex">
                <BotaoDestrutivo
                  rotulo="Desativar administrador"
                  pergunta={`Desativar ${a.nome} da administração da plataforma?`}
                  consequencia="A sessão dele cai agora, e ele não entra de novo até ser reativado. O que ele fez continua na trilha."
                  rotuloConfirmar="Desativar administrador"
                  {...(situacao.isPending ? { motivoIndisponivel: 'Espere o pedido anterior terminar.' } : {})}
                  aoConfirmar={() => {
                    situacao.mutate(a);
                  }}
                />
              </HistoriaNaMesa>
            ) : (
              <Botao
                nivel="terciario"
                disabled={situacao.isPending}
                onClick={() => {
                  situacao.mutate(a);
                }}
              >
                Reativar administrador
              </Botao>
            )}
          </AcaoDaLinha>
        ),
    },
  ];

  return (
    <>
      <CabecalhoPagina
        sobretitulo="Administração da plataforma"
        titulo="Administradores"
        subtitulo="Quem entra aqui. Desativar derruba a sessão na hora; ninguém muda a própria situação, e a plataforma nunca fica sem administrador ativo."
        acoes={
          <HistoriaNaMesa codigo="BTN-PLT-02" className="flex">
            <Botao
              nivel="primario"
              onClick={() => {
                setConvidando(true);
              }}
            >
              Convidar administrador
            </Botao>
          </HistoriaNaMesa>
        }
      />

      {convite ? (
        <EnderecoParaEntregar
          titulo="Convite de administrador da plataforma"
          endereco={enderecoDoConviteDePlataforma(window.location.origin, convite.codigo)}
          para={convite.email}
          expiraEm={convite.expiraEm}
          depois="Quem abrir escolhe a senha da plataforma, que não é a senha de empresa nenhuma."
          aoFechar={() => {
            setConvite(null);
          }}
        />
      ) : null}

      <Tabela
        rotulo="Administradores da plataforma"
        colunas={colunas}
        linhas={paginacao.visiveis}
        chaveDaLinha={(a) => a.administradorId}
        alturaDupla
        carregando={lista.isPending}
        erro={
          lista.isError ? (
            <ErroDaLista oQue="Os administradores" erro={lista.error} aoTentarDeNovo={() => void lista.refetch()} />
          ) : null
        }
        vazio={<EstadoVazio titulo="Nenhum administrador listado" descricao="Recarregue a página; se continuar, entre outra vez na administração." />}
        rodape={<RodapeTabela {...paginacao.rodape} />}
      />

      <Janela
        titulo="Convidar administrador"
        aberta={convidando}
        aoFechar={() => {
          setConvidando(false);
        }}
      >
        <ConviteDeAdministrador
          aoCancelar={() => {
            setConvidando(false);
          }}
          aoConvidar={(c) => {
            setConvite(c);
            setConvidando(false);
          }}
        />
      </Janela>
    </>
  );
}

/** Convidar administrador, na janela (R01, p.29: dois campos, tarefa curta). */
function ConviteDeAdministrador({
  aoCancelar,
  aoConvidar,
}: {
  readonly aoCancelar: () => void;
  readonly aoConvidar: (convite: { email: string; codigo: string; expiraEm: string }) => void;
}) {
  const cliente = useQueryClient();
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const convidar = useMutation({
    mutationFn: () => api.convidarAdministrador(nome.trim(), email.trim()),
    /* A recusa fica no aviso do topo do formulário (p.22), e não repete no toast. */
    meta: {
      retorno: retornoDe<{ expiraEm: string }>((r) => ({
        titulo: 'Convite de administrador emitido',
        alvo: { nome: nome.trim() },
        fatos: [email.trim().toLowerCase()],
        efeito: `O convite vale até ${formatarDataHora(new Date(r.expiraEm))}`,
      })),
      silenciarFalha: true,
    },
    onSuccess: (r) => {
      aoConvidar({ email: email.trim().toLowerCase(), codigo: r.codigo, expiraEm: r.expiraEm });
      void cliente.invalidateQueries({ queryKey: CHAVE });
    },
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    convidar.mutate();
  }

  return (
    <form className="formulario-grade" onSubmit={enviar}>
      {convidar.isError ? (
        <div className="campo-linha">
          <Recado erro={explicarFalha(convidar.error)} />
        </div>
      ) : null}
      <div className="campo-largo">
        <CampoTexto rotulo="Nome" valor={nome} maxLength={120} aoMudar={setNome} />
      </div>
      <CampoTexto
        rotulo="E-mail"
        valor={email}
        tipo="email"
        maxLength={320}
        ajuda="Para quem já foi administrador, o convite reativa e troca a senha."
        aoMudar={setEmail}
      />
      <p className="txt-apoio campo-linha text-tinta-secundaria">
        Quem administra a plataforma cria empresas e gere as pessoas de todas elas. Convide só quem opera o sistema.
      </p>
      <div className="barra-de-acoes campo-linha">
        <Botao nivel="secundario" onClick={aoCancelar}>
          Voltar sem convidar
        </Botao>
        <Botao nivel="primario" type="submit" disabled={convidar.isPending || nome.trim() === '' || email.trim() === ''}>
          {convidar.isPending ? 'Convidando…' : 'Emitir convite'}
        </Botao>
      </div>
    </form>
  );
}

// ------------------------------------------------------------ trilha

function Trilha() {
  const trilha = useQuery({ queryKey: [...CHAVE, 'trilha'], queryFn: () => api.trilha() });
  const paginacao = usePaginacao(trilha.data ?? []);
  return (
    <>
      <CabecalhoPagina
        sobretitulo="Administração da plataforma"
        titulo="Trilha da plataforma"
        subtitulo="Cada ato de quem administra a plataforma, do mais recente para o mais antigo. O que mexeu dentro de uma empresa também está na trilha dela."
      />
      {/* A falha mora NA tabela (p.32), e nao numa caixa acima dela com
          "Nenhum ato registrado" embaixo: as duas juntas diziam coisas opostas. */}
      <TabelaDaTrilha
        atos={paginacao.visiveis}
        carregando={trilha.isPending}
        erro={
          trilha.isError ? (
            <ErroDaLista oQue="Os atos da trilha" erro={trilha.error} aoTentarDeNovo={() => void trilha.refetch()} />
          ) : null
        }
        rodape={<RodapeTabela {...paginacao.rodape} />}
      />
    </>
  );
}

function TabelaDaTrilha({
  atos,
  carregando,
  erro = null,
  rodape = null,
}: {
  readonly atos: readonly AtoDaPlataforma[];
  readonly carregando: boolean;
  readonly erro?: ReactNode;
  readonly rodape?: ReactNode;
}) {
  const colunas: readonly Coluna<AtoDaPlataforma>[] = [
    { chave: 'quando', rotulo: 'Quando', celula: (a) => <span className="tabular-nums">{formatarDataHora(new Date(a.ocorridoEm))}</span> },
    { chave: 'ato', rotulo: 'Ato', celula: (a) => rotuloDoAto(a.acao) },
    { chave: 'empresa', rotulo: 'Empresa', celula: (a) => a.empresa ?? '—' },
    { chave: 'quem', rotulo: 'Quem', celula: (a) => a.administrador },
    { chave: 'detalhe', rotulo: 'Detalhe', celula: (a) => <span className="txt-apoio text-tinta-secundaria">{complementoDoAto(a)}</span> },
  ];
  return (
    <Tabela
      rotulo="Trilha da plataforma"
      colunas={colunas}
      linhas={atos}
      chaveDaLinha={(a) => `${a.ocorridoEm}-${a.acao}-${a.administrador}-${complementoDoAto(a)}`}
      carregando={carregando}
      erro={erro}
      vazio={<EstadoVazio titulo="Nenhum ato registrado ainda" descricao="O primeiro ato aparece aqui assim que uma empresa for criada ou uma pessoa for gerida." />}
      rodape={rodape}
    />
  );
}
